import { F0Icon } from '@factorialco/f0-react'
import { LockLocked } from '@factorialco/f0-react/icons/app'
import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'
import type { TeamQuery } from 'generated/resources/graphql'
import moment from 'moment'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import Settings from './_Settings'

type TeamMembership = NonNullable<NonNullable<TeamQuery['teams']['team']>['memberships']>[number]

interface Props {
  membership: TeamMembership
  canSeeJobCatalog: boolean
  canSeeJobCatalogTree: boolean
  canSeeJobTitle: boolean
  hasLegacyJobCatalog: boolean
  readonly?: boolean
}

const Item = ({
  membership,
  canSeeJobCatalog,
  canSeeJobCatalogTree,
  canSeeJobTitle,
  hasLegacyJobCatalog,
  readonly,
}: Props) => {
  const { navigation } = useNavigation()
  const employee = membership.employee
  const referenceContract = employee.referenceContract
  const jobCatalogLevel = referenceContract?.jobCatalogLevel
  const jobCatalogTreeLevel = referenceContract?.jobCatalogTreeLevel

  const hiredOn = referenceContract?.startsOn ? moment(referenceContract.startsOn).fromNow() : '-'

  const assertCellEllipsis = (text: string, maxCharacters = 8): boolean => {
    return text.length > maxCharacters
  }

  const fitText = (text: string) => {
    return (
      <Text.Normal
        weight="medium"
        whiteSpace="nowrap"
        ellipsis={assertCellEllipsis(text || '', 14)}
      >
        {text}
      </Text.Normal>
    )
  }

  const getJobPosition = () => {
    if (!canSeeJobTitle) {
      return { isLocked: true, value: '' }
    }

    const canSeeEmployeeJobTitle = referenceContract?._policies?.seeJobTitle?.can ?? false

    if (!canSeeEmployeeJobTitle) {
      return { isLocked: true, value: '' }
    }

    const preferredJobTitle = referenceContract?.jobTitle ?? ''

    if (preferredJobTitle) {
      return { isLocked: false, value: preferredJobTitle }
    }

    if (hasLegacyJobCatalog) {
      if (!canSeeJobCatalog) {
        return { isLocked: true, value: '' }
      }

      const role = jobCatalogLevel?.role

      if (!role || role.archived) {
        return { isLocked: false, value: '' }
      }

      const levelName = jobCatalogLevel?.isDefault ? '' : (jobCatalogLevel?.name ?? '')
      return {
        isLocked: false,
        value: levelName ? `${role.name} / ${levelName}` : role.name,
      }
    }

    if (!canSeeJobCatalogTree) {
      return { isLocked: true, value: '' }
    }

    if (!jobCatalogTreeLevel?.ancestor?.name || !jobCatalogTreeLevel.name) {
      return { isLocked: false, value: '' }
    }

    return {
      isLocked: false,
      value: `${jobCatalogTreeLevel.ancestor.name} / ${jobCatalogTreeLevel.name}`,
    }
  }

  const managerFullName = employee.manager
    ? `${employee.manager.firstName} ${employee.manager.lastName}`
    : ''
  const jobPosition = getJobPosition()

  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <Box flexDirection="row" gap="s24" alignItems="center">
          <UserAvatar
            size="s40"
            user={{
              fullName: `${employee.firstName} ${employee.lastName}`,
              avatar: employee?.avatar?.url ?? '',
            }}
          />
          {fitText(
            getEmployeeDisplayName({
              fullName: `${employee.firstName} ${employee.lastName}`,
              lastName: employee.lastName,
              preferredName: employee.preferredName ?? undefined,
            })
          )}
        </Box>
      </Table.Cell>
      {canSeeJobTitle && (
        <Table.Cell>
          {jobPosition.isLocked ? <F0Icon icon={LockLocked} /> : fitText(jobPosition.value)}
        </Table.Cell>
      )}
      <Table.Cell>
        <Text.Normal weight="normal">{hiredOn}</Text.Normal>
      </Table.Cell>
      <Table.Cell colSpan={2}>{fitText(managerFullName)}</Table.Cell>
      <Table.Cell>
        <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
          {membership.id && <Settings membershipId={membership.id} readonly={readonly} />}
          <Button.Router
            to={navigation.employees.id.fullPath({ id: employee.id })}
            type="secondary"
            iconName="arrow-right"
          />
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default Item
